import React, { useCallback } from "react";
import isEmpty from "lodash/isEmpty";
import { usePathname, useRouter } from "next/navigation";

import getDispensariesV3 from "@/api/requests/consumerApi/getDispensariesV3";
import { getDeliveryCopyBlock } from "@/api/requests/directus/getDeliveryCopyBlock";
import { getMetaData } from "@/api/requests/directus/getMetaData";
import getCityStateGeocode, {
  CityInfo,
} from "@/api/requests/getCityStateGeocode";
import getDeliveryCities from "@/api/requests/getDeliveryCities";
import getFinderCitiesNearMe from "@/api/requests/getFinderCitiesNearMe";
import { getUserLocationData } from "@/api/requests/getUserLocation";
import { Action, Category } from "@/constants/events";
import { stateData } from "@/constants/states";
import { City } from "@/custom-types/CitiesNearMe";
import {
  DeliveryCities,
  DeliveryCopyBlock as DeliveryCopyBlockType,
  FAQ,
} from "@/custom-types/Directus";
import { DispensaryV3 } from "@/custom-types/DispensariesV3";
import { MenuItem } from "@/custom-types/MenuItem";
import { RetailType } from "@/custom-types/Store";
import useCustomLocationPickerUpdateBehavior from "@/hooks/useCustomLocationPickerUpdateBehavior";
import { useEventTracker } from "@/hooks/useEventTracker";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { getStateOrProvinceLongName } from "@/utils/getStatesForCountry";
import { getCountryCodeHeader } from "@/utils/requestContext";
import slugify from "@/utils/slugifyURLComponent";

import AddressCaptureModal from "@/components/AddressCaptureModal";
import Breadcrumb from "@/components/Breadcrumb";
import Breadcrumbs from "@/components/Breadcrumbs";
import DealsTakeover from "@/components/DealsTakeover";
import DeliveryCopyBlock from "@/components/DeliveryGateway/DeliveryCopyBlock/DeliveryCopyBlock";
import DeliveryDispensaryCards from "@/components/DeliveryGateway/DeliveryDispensaryCards";
import DeliveryFromLeafly from "@/components/DeliveryGateway/DeliveryFromLeafly";
import DeliveryHead from "@/components/DeliveryGateway/DeliveryHead";
import ReadyForDelivery from "@/components/DeliveryGateway/ReadyForDelivery";
import DispensaryFulfillmentInfo from "@/components/DispensaryFulfillmentInfo";
import FAQs from "@/components/FAQ";
import PopularCities from "@/components/PopularCities/PopularCities";
import RecentlyViewedMenuItems from "@/components/Shared/RecentlyViewedMenuItems";
import TrackImpression from "@/components/TrackImpression";

/**
 * Use a large take to load "all" dispensaries.
 * 100 is a secret limit in finder-service; larger values will be rejected.
 * Menu items will only be loaded for visible dispos.
 */
const MAX_DISPENSARY_TAKE = 50;

/** finder-service returns a slightly different data format than Directus,
 *  this will add name and link so we can plug into the PopularCities card
 */
const formatCities = (cities: City[]) =>
  cities.map((item) => {
    const longName = getStateOrProvinceLongName(item.state);
    return {
      ...item,
      count: String(item.count),
      link: longName
        ? `/delivery/${slugify(longName)}/${slugify(item.city)}`
        : "/delivery",
      name: `${item.city}, ${item.state}`,
    };
  });

const stateToCity = (
  data?: (typeof stateData)[keyof typeof stateData],
): CityInfo | undefined => {
  if (!data) {
    return undefined;
  }
  const { coordinates, country_code, radius, slug, state, stateCode } = data;
  return coordinates && country_code && radius && state && stateCode
    ? {
        boundedRadius: radius,
        coordinates,
        countryCode: country_code,
        state: stateCode,
        stateLong: state,
        stateSlug: slug,
      }
    : undefined;
};

type DeliveryPageProps = {
  dispensaries: DispensaryV3[];
  metadata: FAQ | null;
  cities: DeliveryCities["delivery_cities"];
  copyBlocks: DeliveryCopyBlockType["copy_block"];
  geoCityResult?: CityInfo;
};

const DeliveryPage: React.FC<DeliveryPageProps> = ({
  dispensaries,
  metadata,
  cities,
  copyBlocks,
  geoCityResult,
}) => {
  const pathname = usePathname();
  const { replace } = useRouter();
  const { publishEvent, trackEvent } = useEventTracker();

  const [modalVisible, setModalVisible] = React.useState(false);

  const modalReturnRef = React.createRef<HTMLButtonElement>();

  const hasGeoState = geoCityResult?.stateLong && geoCityResult?.stateSlug;
  const hasGeoCity = geoCityResult?.city && geoCityResult?.citySlug;

  const sharedRecentlyViewedProps = (menuItem?: MenuItem) => ({
    brandId: menuItem?.brand?.id,
    category: Category.deliveryGateFlow,
    dispensaryId: menuItem?.dispensaryId,
    label: "recently viewed menu item card",
    menuItemId: menuItem?.id,
    productId: menuItem?.product?.id,
  });

  const heading = React.useMemo(() => {
    if (dispensaries.length === 0) {
      return "";
    }

    if (metadata?.h1_override) {
      return metadata.h1_override;
    } else {
      const locationHeading =
        geoCityResult?.city && geoCityResult?.stateLong
          ? `${geoCityResult?.city}, ${geoCityResult?.stateLong}`
          : geoCityResult?.stateLong;

      if (locationHeading) {
        return `Order weed delivery in ${locationHeading}`;
      } else {
        return "Order weed delivery near you";
      }
    }
  }, [geoCityResult, metadata, dispensaries]);

  const handleLocationUpdate = useCallback(() => {
    const [_, routeRoot, _routeLocaleSlug, _routeSubLocaleSlug] =
      pathname?.split("/") || [];

    const destinationPath = `/${routeRoot}`;
    replace(destinationPath);
  }, [pathname, replace]);

  useCustomLocationPickerUpdateBehavior(handleLocationUpdate);

  return (
    <>
      <DeliveryHead metadata={metadata} geoCityResult={geoCityResult} />

      {!!dispensaries.length && (
        <section className="container my-xxl">
          <h2 className="my-lg text-md font-extrabold">{heading}</h2>
          <DeliveryDispensaryCards dispensaries={dispensaries} />
        </section>
      )}

      <DeliveryCopyBlock className="my-xxl" copyBlocks={copyBlocks} />

      <DeliveryFromLeafly className="my-xxl" showCTA={true} />

      {!!dispensaries.length && (
        <>
          <DealsTakeover
            ctaLabel="shop deals in your area"
            gaCategory={Category.deliveryGateFlow}
            gaLabel="deals block"
            handleClick={() => setModalVisible(true)}
            hideLocation={true}
            signupSource="delivery_deals"
            className="!my-xxl"
          />
          <AddressCaptureModal
            isVisible={modalVisible}
            onDismissed={() => setModalVisible(false)}
            returnFocusRef={modalReturnRef}
          />
        </>
      )}

      {metadata?.faqs?.length ? (
        <TrackImpression
          category={Category.deliveryGateFlow}
          label="faq block"
          className="my-xxl empty:hidden"
        >
          <FAQs
            data={metadata?.faqs}
            onEntryClick={() =>
              trackEvent(Category.deliveryGateFlow, Action.click, "faq block")
            }
            initialShowPanel={true}
          />
        </TrackImpression>
      ) : (
        <> </>
      )}

      <ReadyForDelivery />

      <PopularCities
        cities={cities}
        title="Weed delivery near you"
        subtitle="Shop Leafly delivery for local, legal weed, anywhere you are."
        className="my-xxl"
        gaCategory={Category.deliveryGateFlow}
        gaLabel="location block"
      />

      <RecentlyViewedMenuItems
        className="my-section"
        carouselClassName="container"
        onCardClick={(menuItem: MenuItem) =>
          publishEvent({
            action: Action.Click,
            ...sharedRecentlyViewedProps(menuItem),
          })
        }
        onCardImpression={(menuItem: MenuItem) =>
          publishEvent({
            action: Action.impression,
            nonInteractionEvent: true,
            ...sharedRecentlyViewedProps(menuItem),
          })
        }
        menuItemProps={{
          renderFulfillmentInfo: (dispensary) => (
            <DispensaryFulfillmentInfo dispensary={dispensary} />
          ),
          showFulfillment: false,
        }}
      />

      {hasGeoState && (
        <Breadcrumbs>
          <Breadcrumb href="/delivery">Delivery</Breadcrumb>
          <Breadcrumb
            currentLocation={!hasGeoCity}
            href={`/delivery/${geoCityResult.stateSlug}`}
          >
            {geoCityResult.stateLong}
          </Breadcrumb>
          {hasGeoCity && (
            <Breadcrumb
              currentLocation={true}
              href={`/delivery/${geoCityResult.stateSlug}/${geoCityResult.citySlug}`}
            >
              {geoCityResult.city}
            </Breadcrumb>
          )}
        </Breadcrumbs>
      )}
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<DeliveryPageProps>(
  async (context) => {
    const slugs = (context.query.slug as string[]) || [];

    if (slugs.length > 2) {
      return {
        notFound: true,
      };
    }

    const countryCode = getCountryCodeHeader(context.req.headers);
    const location = await getUserLocationData(countryCode, context);

    const stateSlug = slugs.length >= 1 && slugs[0];
    const citySlug = slugs.length === 2 && slugs[1];

    let geoCityResult;
    if (stateSlug && citySlug) {
      geoCityResult = await getCityStateGeocode({
        city: String(citySlug),
        state: String(stateSlug),
      });
    } else if (stateSlug && !citySlug) {
      // Prefer hand-curated results if available
      geoCityResult = stateToCity(
        stateData[String(stateSlug).toLowerCase() as keyof typeof stateData],
      );

      if (!geoCityResult) {
        geoCityResult = await getCityStateGeocode({
          city: "",
          state: String(stateSlug),
        });
      }
    }

    const isSEODeliveryQuery = !!geoCityResult;
    const coords = geoCityResult
      ? geoCityResult.coordinates
      : location?.coordinates
        ? {
            lat: location.coordinates.latitude,
            lon: location.coordinates.longitude,
          }
        : undefined;

    const dispensariesData = await getDispensariesV3({
      ...coords,
      ...(isSEODeliveryQuery && geoCityResult?.boundedRadius
        ? {
            radius: `${geoCityResult.boundedRadius}mi`,
          }
        : {}),
      deliversToPoint: !isSEODeliveryQuery,
      flags: ["delivery", "dispensary", countryCode],
      merchUnitName: "delivery-platinum-placement",
      sort:
        (context.query.dispensarySort &&
          String(context.query.dispensarySort)) ||
        "default",
      take: MAX_DISPENSARY_TAKE,
    });

    const dispensaries = [
      ...dispensariesData.sponsoredStores,
      ...dispensariesData.stores,
    ];

    const path = `/delivery${stateSlug ? `/${stateSlug}` : ""}${
      citySlug ? `/${citySlug}` : ""
    }`;

    const [metadata = null, deliveryCities, copyBlocks] = await Promise.all([
      getMetaData("faq", path, countryCode),
      getDeliveryCities(countryCode, citySlug || stateSlug ? path : null),
      getDeliveryCopyBlock("delivery_copy_block", path, countryCode),
    ]);

    let cities: DeliveryCities["delivery_cities"] = [];
    if (!deliveryCities.delivery_cities?.length) {
      const citiesNearby = await getFinderCitiesNearMe({
        countryCode,
        delivery_enabled: true,
        location: {
          coordinates: coords,
        },
        retailType: "dispensary" as RetailType,
      });

      cities = formatCities(citiesNearby) || [];
    } else {
      cities = deliveryCities.delivery_cities;
    }

    // TODO: WAITING ON TEST TO RUN
    const deliveryDetailsOnRetailerCardsFlag = true;

    return {
      props: {
        cities,
        copyBlocks: copyBlocks?.copy_block || [],
        disableFixedHeader: true, // passed to _app
        dispensaries,
        ...(!isEmpty(geoCityResult) && { geoCityResult }),
        deliveryDetailsOnRetailerCardsFlag,
        metadata,
      },
    };
  },
);

export default DeliveryPage;
